import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Checkbox, FormControlLabel, Typography, Snackbar, Alert, CircularProgress } from "@mui/material";
import { Icon } from "@iconify/react";
import BottomSection from "../components/BottomSection"
import { postDataToAPI } from "../services/ApiService";

function MailServerScan() {
  const [email, setEmail] = useState("");
  const [domain, setDomain] = useState("");
  const [tosChecked, setTosChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  // const siteKey = "6LcO6MAqAAAAAPfx2omRucOjT-As_qkxu3L_b0l-"; // Replace with your reCAPTCHA v2 site key
  const siteKey = "6Ld8IcIqAAAAAOSN4Isuy7Udbt8VFzTe-5y0vD-D"; // Replace with your reCAPTCHA v2 site key

  const validateFields = () => {
    const fieldErrors = {};
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      fieldErrors.email = "Please enter a valid email address.";
    }
    if (!domain || !/^(https?:\/\/)?([a-zA-Z0-9-_]+(\.[a-zA-Z0-9-_]+)+.*)$/.test(domain)) {
      fieldErrors.domain = "Please enter a valid domain (e.g., example.com).";
    }
    if (!tosChecked) {
      fieldErrors.tos = "You must agree to the Terms of Service.";
    }
    setErrors(fieldErrors);

    return Object.keys(fieldErrors).length === 0;
  };

  const handleCaptchaSuccess = (token) => {
    setCaptchaToken(token);
    console.log("Captcha Token:", token);
    setIsSubmitDisabled(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;

    console.log(
      `MailServer Scan Submitted:\nEmail: ${email}\ndomain: ${domain}\nTOS Accepted: ${tosChecked}\nCaptcha Token: ${captchaToken}`
    );
    let payload = {
      'email': email,
      'domain': domain,
      'reCaptchaToken': captchaToken
    };

    const result = await postDataToAPI('mailservercheck', payload, setLoading, setToast);
    if (result) setFormSubmitted(true);
  };

  useEffect(() => {
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        window.grecaptcha.render("captcha-container", {
          sitekey: siteKey,
          callback: handleCaptchaSuccess,
        });
      });
    }
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      bgcolor="#f9fafb"
      p={2}
      sx={{ paddingBottom: "3rem" }}
    >
      {/* Top Text Outside the Panel */}
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h2"
          fontWeight="bold"
          gutterBottom
          sx={{ color: "#000" }}
        >
          <Icon icon={`lucide:mail-check`} width={45} height={45} style={{ marginRight: "16px", color: "rgb(0, 145, 70)" }} />Mail Server Checker
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "#333", maxWidth: 800, fontSize: "1.2rem" }}
        >
          This free online tool checks mail server configuration and security. It also checks for SPF and DKIM keys.
        </Typography>
      </Box>

      {/* Rectangle Panel */}
      {(!formSubmitted) ? (<Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          backgroundColor: "#fff",
          padding: 4,
          borderRadius: "8px",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
          width: "100%",
          maxWidth: 800,
        }}
      >
        <TextField
          fullWidth
          label="Email"
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={Boolean(errors.email)}
          helperText={errors.email}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "rgb(0, 145, 70)",
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Domain"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          error={Boolean(errors.domain)}
          helperText={errors.domain}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": {
                borderColor: "rgb(0, 145, 70)",
              },
            },
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={tosChecked}
              onChange={(e) => setTosChecked(e.target.checked)}
              sx={{
                color: "rgb(0, 145, 70)",
                "&.Mui-checked": {
                  color: "rgb(0, 145, 70)",
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              I have read and agree to the{" "}
              <a href="https://portend.ai/terms-and-conditions/" target="_blank" style={{ color: "rgb(0, 145, 70)", textDecoration: "none" }}>
                Terms of Service
              </a>
              .
            </Typography>
          }
        />
        {errors.tos && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 1, mt: -1, display: "block" }}
          >
            {errors.tos}
          </Typography>
        )}
        <div
          id="captcha-container"
          style={{
            marginBottom: "16px",
            display: "block",
            width: "90%",
          }}
        ></div>
        <Button type="submit" variant="contained" disabled={isSubmitDisabled || loading} sx={{
          mt: 2, backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)",
          color: isSubmitDisabled ? "#888" : "#fff", padding: "12px 0",
          width: "300px", fontWeight: "bold",
          "&:hover": { backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)" }
        }}>
          {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Submit"}
        </Button>

        {(toast.severity === 'error') && <Typography variant="body2" sx={{ my: 2, fontSize: '1.5rem', background: '#ba1111', color: '#fff', padding: '5px', borderRadius: '5px' }}>{toast.message} <IconButton sx={{ float: 'right', paddingTop: '5px', color: '#fff' }} onClick={() => setToast({})}><Icon icon="uil:times" width="24" height="24" /></IconButton></Typography>}
      </Box>) : (<Box
        component="div"
        sx={{
          backgroundColor: "#fff",
          padding: 4,
          borderRadius: "8px",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
          width: "100%",
          maxWidth: 800,
        }}
      ><Typography
        variant="body1"
        sx={{ color: "#333", textAlign: 'center', fontSize: "1.5rem" }}
      >
          Thank you. Your request has been submitted. You will be informed by email when the results are available. </Typography>
      </Box>)}

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default MailServerScan;
